import { useEffect, useState, type RefObject } from 'react'
import { ChevronDown, ChevronRight, Copy, Check, Globe, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { BasecampIcon } from '@/components/icons/basecamp-icon'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { getDocument, updateDocument } from '@/lib/api'
import { QUIET_OVERFLOW_MENU_CONTENT_COMPACT_CLASS } from '@/lib/menu-chrome'
import {
  BASECAMP_DRAFT_STATUSES,
  appendBasecampDraftFence,
  basecampDraftPreviewHtml,
  copyBasecampDraft,
  documentHasBasecampDraft,
  isBasecampTargetUrl,
  parseBasecampDraftFence,
  type BasecampDraft,
} from '@/lib/basecamp-draft-fence'
import { testIds } from '@/lib/test-ids'
import { cn } from '@/lib/utils'

type BasecampDraftFenceViewProps = {
  source: string
  language?: string
  className?: string
  /** When set, the status control writes `status:` back into the document. */
  onStatusChange?: (fenceSource: string, status: string) => void
}

const CARD_CLASS =
  'basecamp-draft-fence not-prose my-4 min-w-0 max-w-full overflow-hidden rounded-lg border border-border border-l-2 border-l-primary bg-card text-foreground'

const HEADER_CONTROL =
  'inline-flex h-7 shrink-0 items-center justify-center gap-1 rounded-md px-1.5 text-muted-foreground hover:bg-muted hover:text-foreground'

const BODY_CLASS =
  'basecamp-draft-fence-body min-w-0 max-w-full whitespace-normal break-words px-4 py-3 text-sm leading-6 text-foreground [overflow-wrap:anywhere]'

const BODY_STYLE = {
  whiteSpace: 'normal',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
  maxWidth: '100%',
} as const

/** Review card for a ```yaml basecamp-draft fence. Copy writes comment-composer HTML. */
export function BasecampDraftFenceView({
  source,
  language = '',
  className,
  onStatusChange,
}: BasecampDraftFenceViewProps) {
  const draft = parseBasecampDraftFence(source, language)
  const [copied, setCopied] = useState(false)
  const [failed, setFailed] = useState(false)

  if (!draft) {
    return (
      <div
        className={cn(CARD_CLASS, 'px-3 py-2 text-sm text-muted-foreground', className)}
        data-testid={testIds.basecampDraftFence.root}
      >
        Basecamp draft (unparseable)
      </div>
    )
  }

  const onCopy = async () => {
    const ok = await copyBasecampDraft(draft)
    setFailed(!ok)
    setCopied(ok)
    window.setTimeout(() => {
      setCopied(false)
      setFailed(false)
    }, 2000)
  }

  return (
    <BasecampDraftCard
      draft={draft}
      source={source}
      className={className}
      copied={copied}
      failed={failed}
      onStatusChange={onStatusChange}
      onCopy={() => {
        void onCopy()
      }}
    />
  )
}

function stopHeaderEvent(event: { stopPropagation: () => void; preventDefault?: () => void }) {
  event.stopPropagation()
}

function DraftTargetLink({ url }: { url: string }) {
  const basecamp = isBasecampTargetUrl(url)
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      title="Where to post"
      aria-label="Where to post"
      data-testid={testIds.basecampDraftFence.target}
      className={cn(HEADER_CONTROL, 'w-7')}
      onMouseDown={stopHeaderEvent}
      onClick={stopHeaderEvent}
      onDoubleClick={stopHeaderEvent}
    >
      {basecamp ? <BasecampIcon className="h-4 w-4" /> : <Globe className="size-4" />}
    </a>
  )
}

function statusChoices(current: string): string[] {
  if (!current || (BASECAMP_DRAFT_STATUSES as readonly string[]).includes(current)) {
    return [...BASECAMP_DRAFT_STATUSES]
  }
  return [current, ...BASECAMP_DRAFT_STATUSES]
}

function DraftStatusControl({
  source,
  status,
  onStatusChange,
}: {
  source: string
  status: string
  onStatusChange?: (fenceSource: string, status: string) => void
}) {
  if (!onStatusChange) {
    return status ? (
      <span className={cn(HEADER_CONTROL, 'cursor-default px-1.5 text-xs hover:bg-transparent')}>
        {status}
      </span>
    ) : null
  }
  const current = status || 'not posted'
  const posted = current === 'posted'
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Draft status"
          data-testid={testIds.basecampDraftFence.status}
          className={cn(HEADER_CONTROL, 'text-xs', posted && 'text-primary')}
          onMouseDown={stopHeaderEvent}
          onDoubleClick={stopHeaderEvent}
        >
          {current}
          <ChevronDown className="size-3 opacity-60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className={QUIET_OVERFLOW_MENU_CONTENT_COMPACT_CLASS}
        onCloseAutoFocus={(event) => event.preventDefault()}
      >
        {statusChoices(current).map((choice) => (
          <DropdownMenuItem
            key={choice}
            onSelect={() => {
              if (choice !== current) onStatusChange(source, choice)
            }}
          >
            <Check className={cn('size-3.5', choice === current ? 'opacity-100' : 'opacity-0')} />
            {choice}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function BasecampDraftCard({
  draft,
  source,
  className,
  copied,
  failed,
  onCopy,
  onStatusChange,
}: {
  draft: BasecampDraft
  source: string
  className?: string
  copied: boolean
  failed: boolean
  onCopy: () => void
  onStatusChange?: (fenceSource: string, status: string) => void
}) {
  const posted = draft.status === 'posted'
  const [open, setOpen] = useState(!posted)
  useEffect(() => {
    setOpen(!posted)
  }, [posted])

  return (
    <div className={cn(CARD_CLASS, className)} data-testid={testIds.basecampDraftFence.root}>
      <div
        className={cn(
          'flex items-center justify-between gap-2 px-2 py-1.5',
          open && 'border-b border-border',
        )}
      >
        <div className="flex min-w-0 items-center gap-1.5">
          <button
            type="button"
            aria-expanded={open}
            aria-label={open ? 'Collapse draft' : 'Expand draft'}
            title={open ? 'Collapse draft' : 'Expand draft'}
            className={cn(HEADER_CONTROL, 'w-7')}
            onMouseDown={stopHeaderEvent}
            onDoubleClick={stopHeaderEvent}
            onClick={(event) => {
              event.stopPropagation()
              setOpen((value) => !value)
            }}
          >
            {open ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
          </button>
          <span className="shrink-0 rounded-md bg-primary/10 px-1.5 py-0.5 text-[11px] font-medium text-primary">
            Basecamp draft
          </span>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <DraftStatusControl
            source={source}
            status={draft.status}
            onStatusChange={onStatusChange}
          />
          {draft.targetUrl ? <DraftTargetLink url={draft.targetUrl} /> : null}
          <button
            type="button"
            className={cn(HEADER_CONTROL, 'w-7')}
            aria-label={failed ? 'Copy failed' : copied ? 'Copied' : 'Copy for Basecamp'}
            title={failed ? 'Copy failed' : copied ? 'Copied' : 'Copy for Basecamp'}
            data-testid={testIds.basecampDraftFence.copy}
            onMouseDown={(event) => event.preventDefault()}
            onClick={onCopy}
          >
            {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
          </button>
        </div>
      </div>
      {open ? (
        <div
          className={BODY_CLASS}
          style={BODY_STYLE}
          data-testid={testIds.basecampDraftFence.body}
          dangerouslySetInnerHTML={{ __html: basecampDraftPreviewHtml(draft.body) }}
        />
      ) : null}
    </div>
  )
}

/** Chat CTA: append this fence to the open work spec. */
export function BasecampDraftAddToDocument({
  fenceSource,
  documentId,
  documentTitle,
  documentIdRef,
  documentTitleRef,
}: {
  fenceSource: string
  documentId?: string | null
  documentTitle?: string | null
  documentIdRef?: RefObject<string | undefined>
  documentTitleRef?: RefObject<string | undefined>
}) {
  const [phase, setPhase] = useState<'idle' | 'adding' | 'added'>('idle')
  const resolveId = () => documentIdRef?.current || documentId || null
  const resolveTitle = () =>
    (documentTitleRef?.current || documentTitle || '').trim() || 'open document'
  const title = resolveTitle()

  const onAdd = async () => {
    const id = resolveId()
    const label = resolveTitle()
    if (!id || phase === 'adding') return
    setPhase('adding')
    try {
      const doc = await getDocument(id)
      const content = doc.content || ''
      if (documentHasBasecampDraft(content, fenceSource)) {
        toast.message(`Already in ${label}`)
        setPhase('added')
        return
      }
      await updateDocument(id, {
        content: appendBasecampDraftFence(content, fenceSource),
        writeToFile: true,
        saveEditHistory: true,
        changedBy: 'user',
        historyMetadata: { channel: 'webapp-chat', saveTrigger: 'basecamp-draft' },
      })
      window.dispatchEvent(new CustomEvent('document-updated', { detail: { documentId: id } }))
      toast.success(`Added to ${label}`)
      setPhase('added')
    } catch (error) {
      setPhase('idle')
      toast.error('Could not add the draft', {
        description: error instanceof Error ? error.message : 'Unknown error',
      })
    }
  }

  if (!resolveId()) {
    return (
      <p className="mt-2 text-xs text-muted-foreground">
        Open the work spec to add this draft.
      </p>
    )
  }

  return (
    <div className="mt-2">
      <Button
        type="button"
        variant="default"
        size="sm"
        className="h-7 gap-1.5 text-xs"
        disabled={phase !== 'idle'}
        onClick={() => {
          void onAdd()
        }}
      >
        {phase === 'adding' ? <Loader2 className="size-3.5 animate-spin" /> : null}
        {phase === 'added' ? `In ${title}` : `Add to ${title}`}
      </Button>
    </div>
  )
}

/** TipTap visual-mode card. Hidden code sibling keeps the fence on round-trip. */
export function buildBasecampDraftFenceDom(text: string, language: string): HTMLElement {
  const draft = parseBasecampDraftFence(text, language)
  const root = document.createElement('div')
  root.className = CARD_CLASS
  root.setAttribute('data-testid', 'basecamp-draft-fence')

  if (!draft) {
    root.className += ' px-3 py-2 text-sm text-muted-foreground'
    root.textContent = 'Basecamp draft (unparseable)'
    return root
  }

  const posted = draft.status === 'posted'
  let open = !posted

  const header = document.createElement('div')
  header.className = 'flex items-center justify-between gap-2 px-2 py-1.5'

  const meta = document.createElement('div')
  meta.className = 'flex min-w-0 items-center gap-1.5'
  const toggle = document.createElement('button')
  toggle.type = 'button'
  toggle.className = HEADER_CONTROL + ' w-7'
  toggle.textContent = open ? '\u25BE' : '\u25B8'
  meta.appendChild(toggle)
  const badge = document.createElement('span')
  badge.className =
    'shrink-0 rounded-md bg-primary/10 px-1.5 py-0.5 text-[11px] font-medium text-primary'
  badge.textContent = 'Basecamp draft'
  meta.appendChild(badge)
  header.appendChild(meta)

  const actions = document.createElement('div')
  actions.className = 'flex shrink-0 items-center gap-1'
  if (draft.status) {
    const status = document.createElement('span')
    status.className = HEADER_CONTROL + ' cursor-default px-1.5 text-xs'
    status.textContent = draft.status
    actions.appendChild(status)
  }
  if (draft.targetUrl) {
    const link = document.createElement('a')
    link.href = draft.targetUrl
    link.target = '_blank'
    link.rel = 'noopener noreferrer'
    link.title = 'Where to post'
    link.setAttribute('aria-label', 'Where to post')
    link.setAttribute('data-testid', 'basecamp-draft-fence-target')
    link.className = HEADER_CONTROL + ' w-7'
    link.textContent = '\u25B2'
    actions.appendChild(link)
  }

  const button = document.createElement('button')
  button.type = 'button'
  button.className = HEADER_CONTROL + ' w-7'
  button.title = 'Copy for Basecamp'
  button.setAttribute('aria-label', 'Copy for Basecamp')
  button.setAttribute('data-testid', 'basecamp-draft-fence-copy')
  button.textContent = '\u2398'
  button.addEventListener('mousedown', (event) => event.preventDefault())
  button.addEventListener('click', (event) => {
    event.preventDefault()
    event.stopPropagation()
    void copyBasecampDraft(draft).then((ok) => {
      button.textContent = ok ? '\u2713' : '!'
      window.setTimeout(() => {
        button.textContent = '\u2398'
      }, 2000)
    })
  })
  actions.appendChild(button)
  header.appendChild(actions)
  root.appendChild(header)

  const body = document.createElement('div')
  body.className = BODY_CLASS
  body.style.whiteSpace = 'normal'
  body.style.overflowWrap = 'anywhere'
  body.style.wordBreak = 'break-word'
  body.style.maxWidth = '100%'
  body.setAttribute('data-testid', 'basecamp-draft-fence-body')
  body.innerHTML = basecampDraftPreviewHtml(draft.body)
  body.style.display = open ? '' : 'none'
  if (open) header.className += ' border-b border-border'
  toggle.addEventListener('click', (event) => {
    event.preventDefault()
    event.stopPropagation()
    open = !open
    body.style.display = open ? '' : 'none'
    toggle.textContent = open ? '\u25BE' : '\u25B8'
    header.className = open
      ? 'flex items-center justify-between gap-2 border-b border-border px-2 py-1.5'
      : 'flex items-center justify-between gap-2 px-2 py-1.5'
  })
  root.appendChild(body)
  return root
}
